import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchGet, fetchPost, fetchDelete } from '../../../../helpers/fetching';
import {
  makeStyles,
  Checkbox,
  FormControlLabel,
  Typography,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import DraggableDialog from '../DraggableDialog/DraggableDialog';
import ExportData from './ExportData';

const useStyle = makeStyles((theme) => ({
  MainContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '100%',
    width: '98%',
  },

  ReviewSelectUsers: {
    width: '40%',
    marginBottom: '10px',
  },

  SectionsContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    height: 'fit-content',
    overflowX: 'scroll',
    overflowY: 'hidden',
    flexDirection: 'column',
    paddingTop: '20px',
    width: '100%',
    borderTop: '1px solid #575757',
  },

  optionsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },

  optionsContainer_data: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    width: '100%',
    flexWrap: 'wrap',
    overflowX: 'scroll',
    gap: '10px',
  },

  EmptyContainer: {
    display: 'none',
  },

  sectionTitle: {
    fontWeight: 'bold',
    fontSize: theme.fonts.responsiveMediumBig,
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.1rem',
  },

  smallContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },

  export_button: {
    background: theme.palette.accents.dark,
    color: 'white',
    minWidth: '200px',
    '&:hover': {
      background: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px rgb(180 180 180 / 78%)',
    },
  },

  export_button_container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    margin: '10px',
    width: '97%',
  },

  spinner_container: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '30px',
    width: '70%',
  },

  spinner_icon: {
    margin: theme.spacing(2),
  },

  spinner_text: {
    color: 'gray',
    fontStyle: 'italic',
  },
}));

const defaultUserList = [{ username: 'bbox' }];

const DataExport = (props) => {
  const classes = useStyle();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  // Store state
  const filter = useSelector((state) => state.filter);
  const quailty_points_export = useSelector((state) => state.global_settings.quailty_points_export);
  const globalSettings = useSelector((store) => store.global_settings ?? {});
  const { totalExaminations } = useSelector((state) => state.examinations);

  const userLang = globalSettings.language ?? i18n.language ?? 'en';

  // Local state
  const [exportImages, setExportImages] = useState(false);
  const [exportData, setExportData] = useState(false);
  const [exportXLS, setExportXLS] = useState(true);
  const [exportOperatorReport, setExportOperatorReport] = useState(false);
  const [users, setUsers] = useState(defaultUserList);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dataStatus, setDataStatus] = useState(null);
  const [studyIDs, setStudyIDs] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handlers
  const handleUserSelection = (event) => {
    setSelectedUsers(event.target.value);
  };

  async function onJobCreate() {
    setLoading(true);
    const body = {
      study_ids: studyIDs,
      language: userLang,
      filters: {
        users: selectedUsers.length === 0 ? [] : selectedUsers,
      },
      flags: {
        data: exportData,
        images: exportImages,
        xls: exportXLS,
        operator_report: exportOperatorReport,
      },
    };
    const response = await fetchPost('export', body, 'data');
    if (!response.success) {
      props.setSnackbar({
        msg: `api_messages.${response.msg}`,
        severity: 'error',
      });

      setTimeout(() => {
        setLoading(false);
      }, 3000);
      return;
    }
    fetchDataStatus();
  }

  async function onJobDelete(id) {
    const response = await fetchDelete(`export/delete/${id}`, {}, 'data');
    if (!response.success) {
      props.setSnackbar({
        msg: `api_messages.${response.msg}`,
        severity: 'error',
      });
      return;
    }
    fetchDataStatus();
  }

  function onClose() {
    props.onClose();
    props.setDataIsReady(null);
  }

  // Fetching
  const fetchDataStatus = async () => {
    const response = await fetchGet('export/status', 'data');
    if (!response.success) {
      props.setSnackbar({
        msg: `api_messages.${response.msg}`,
        severity: 'error',
      });
      return;
    }
    setDataStatus(response.data);
    setLoading(false);
  };

  const fetchUsers = useCallback(async () => {
    const response = await fetchGet('users');
    if (!response.success) return;
    const users = response.data.filter((user) => user.annotation_permission);
    setUsers(users);
  }, []);

  const fetchStudyIDs = async () => {
    const response = await fetchPost('study_ids', { filter });
    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        })
      );
      return;
    }
    setStudyIDs(response.data.map((item) => item.id));
  };

  // Effect
  useEffect(() => {
    props.setDataIsReady(null);
    fetchStudyIDs();
  }, []);

  useEffect(() => {
    fetchDataStatus();
  }, [props.dataIsReady]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <DraggableDialog
      maxWidth='lg'
      title={t('export.exportTitle')}
      open={props.open}
      actions={[
        {
          onClick: onClose,
          color: 'secondary',
          label: t('export.cancel'),
        },
      ]}
      onClose={onClose}>
      <div className={classes.MainContainer}>
        <Typography variant='body1'>{t('export.export_explanation')}</Typography>
        <Typography>
          {t('Settings.affected_studies_count', {
            count: totalExaminations,
          })}
        </Typography>

        {/* Users selection */}
        <div className={classes.SectionsContainer}>
          <Typography className={classes.sectionTitle}>{t('export.creator')}</Typography>
          <div className={classes.smallContainer}>
            <div className={classes.optionsContainer} style={{ width: '70%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedUsers.length === 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers([]);
                      }
                    }}
                  />
                }
                label={t('export.applied')}
              />

              <FormControl className={classes.ReviewSelectUsers}>
                <InputLabel>{t('export.select_users')}</InputLabel>
                <Select
                  multiple
                  value={selectedUsers}
                  onChange={handleUserSelection}
                  label={t('export.select_users')}
                  renderValue={(selected) => {
                    return selected.length === 0 ? '' : selected.join(', ');
                  }}>
                  {users.concat(defaultUserList).map((user) => (
                    <MenuItem key={user.username} value={user.username}>
                      <Checkbox checked={selectedUsers.includes(user.username)} />
                      <span style={{ marginLeft: 8 }}>{user.username}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          {/* Export options */}
          <Typography className={classes.sectionTitle}>{t('export.type')}</Typography>
          <div className={classes.smallContainer}>
            <div className={classes.optionsContainer} style={{ width: '70%' }}>
              <FormControlLabel
                key={'xls'}
                label={<Typography>{t('export.spreadsheet')}</Typography>}
                control={<Checkbox checked={exportXLS} onChange={() => setExportXLS((prev) => !prev)} />}
              />

              {quailty_points_export && (
                <FormControlLabel
                  key='operator_report'
                  label={<Typography>{t('export.operator_report')}</Typography>}
                  control={
                    <Checkbox
                      checked={exportOperatorReport}
                      onChange={() => setExportOperatorReport((prev) => !prev)}
                    />
                  }
                />
              )}

              <FormControlLabel
                key='images'
                label={<Typography>{t('export.images')}</Typography>}
                control={
                  <Checkbox checked={exportImages} onChange={() => setExportImages((prev) => !prev)} />
                }
              />

              <FormControlLabel
                key='data'
                label={<Typography>{t('export.exportResults')}</Typography>}
                control={
                  <Checkbox
                    checked={exportData}
                    onChange={() => setExportData((prev) => !prev)}
                    name='images'
                  />
                }
              />
            </div>
          </div>

          <div className={classes.export_button_container}>
            <div className={classes.spinner_container}>
              {(studyIDs === null || dataStatus === null || loading) && (
                <>
                  <CircularProgress className={classes.spinner_icon} size={28} />
                  <Typography className={classes.spinner_text}>{t('export.extracting_data')}</Typography>
                </>
              )}
            </div>

            <Button
              onClick={onJobCreate}
              className={classes.export_button}
              variant='contained'
              disabled={loading}
              size='small'>
              {t('export.export')}
            </Button>
          </div>
        </div>

        {/* Exported data status */}
        <div
          className={dataStatus?.length > 0 ? classes.SectionsContainer : classes.EmptyContainer}
          style={{ minHeight: '280px', justifyContent: 'flex-start' }}>
          <Typography className={classes.sectionTitle}>{t('export.results')}</Typography>
          <div className={classes.optionsContainer_data}>
            <ExportData dataStatus={dataStatus} onJobDelete={onJobDelete} />
          </div>
        </div>
      </div>
    </DraggableDialog>
  );
};

export default DataExport;
